import { IconAdjustments, IconAngle, IconScale } from "@tabler/icons-react";
import type { components } from "@/api/generated/schema";

export type ScheduleStatus = components["schemas"]["ScheduleSlotStatus"];
export type ScheduleTaskType = components["schemas"]["ScheduleTaskType"];

export const SCHEDULE_TASK_TYPES: { value: ScheduleTaskType; label: string }[] = [
  { value: "checks", label: "Checks (toe + yaw)" },
  { value: "calibration", label: "Calibration" },
  { value: "weigh_in", label: "Weigh-in" },
];

const SHORT_LABEL: Record<ScheduleTaskType, string> = {
  checks: "Checks",
  calibration: "Calibration",
  weigh_in: "Weigh-in",
};

export function taskTypeShort(t: ScheduleTaskType): string {
  return SHORT_LABEL[t] ?? t;
}

// Per-type icon for compact slot rendering (planner blocks + day columns).
// Reuses the app's calibration/weighing iconography.
export const TASK_ICON: Record<ScheduleTaskType, typeof IconAngle> = {
  checks: IconAngle,
  calibration: IconAdjustments,
  weigh_in: IconScale,
};

// What each task type needs signed off. The `kind` matches ScheduleSignoff.kind
// from the backend, so a present sign-off marks a requirement done and the rest
// are "what's missing". Labels mirror the backend sign-off labels.
export const TASK_REQUIREMENTS: Record<ScheduleTaskType, { kind: string; label: string }[]> = {
  checks: [
    { kind: "yaw", label: "Yaw check" },
    { kind: "toe", label: "Toe check" },
  ],
  calibration: [{ kind: "calibration", label: "Calibration (golden)" }],
  weigh_in: [{ kind: "weigh_in", label: "Platform weigh-in" }],
};

export interface ScheduleStatusMeta {
  label: string;
  border: string; // left-accent for rows/cards (ADR-033)
  bg: string; // badge background
  text: string; // badge text
  dot: string; // status dot
}

export function scheduleStatusMeta(s: ScheduleStatus): ScheduleStatusMeta {
  const map: Record<ScheduleStatus, ScheduleStatusMeta> = {
    done: {
      label: "Done",
      border: "border-l-green-500",
      bg: "bg-emerald-50 dark:bg-emerald-950/40",
      text: "text-emerald-700 dark:text-emerald-400",
      dot: "bg-emerald-500",
    },
    in_progress: {
      label: "In progress",
      border: "border-l-amber-500",
      bg: "bg-amber-50 dark:bg-amber-950/40",
      text: "text-amber-700 dark:text-amber-400",
      dot: "bg-amber-500",
    },
    overdue: {
      label: "Overdue",
      border: "border-l-red-500",
      bg: "bg-red-50 dark:bg-red-950/40",
      text: "text-red-700 dark:text-red-400",
      dot: "bg-red-500",
    },
    planned: {
      label: "Planned",
      border: "border-l-blue-400",
      bg: "bg-blue-50 dark:bg-blue-950/40",
      text: "text-blue-700 dark:text-blue-400",
      dot: "bg-blue-400",
    },
  };
  return map[s];
}

// Slot datetimes are floating wall-clock — the venue's local time, stored naive
// (no tz designator). Parse the literal Y-M-D-H-M with no timezone conversion so
// a 09:00 slot reads 09:00 in every browser, and write the same naive form back.
function parseWallClock(iso: string): Date {
  const [datePart, timePart = "00:00"] = iso.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  return new Date(y, mo - 1, d, h, mi);
}

export function formatSlotWhen(iso: string | null | undefined): string {
  if (!iso) return "Unscheduled";
  return parseWallClock(iso).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Time-of-day only (e.g. "9:00 AM") — compact form for the per-day columns. */
export function formatSlotTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return parseWallClock(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** A stored wall-clock datetime → the value a <input type="datetime-local"> expects. */
export function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = parseWallClock(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A datetime-local value → the naive wall-clock string the API stores verbatim. */
export function datetimeLocalToNaive(local: string): string | null {
  if (!local) return null;
  return local.length === 16 ? `${local}:00` : local;
}

// Slots are stored in the event's venue wall-clock, so "now"/"today" must be
// read in the venue tz (carried on the event), not the viewer's browser.
/** Current day-key + minutes-of-day in `tz` (falls back to the viewer's browser). */
export function nowInZone(tz: string | undefined): { dateKey: string; minutes: number } {
  const now = new Date();
  if (!tz) {
    return {
      dateKey: now.toLocaleDateString("en-CA"),
      minutes: now.getHours() * 60 + now.getMinutes(),
    };
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return {
    dateKey: `${get("year")}-${get("month")}-${get("day")}`,
    minutes: (Number(get("hour")) % 24) * 60 + Number(get("minute")),
  };
}

// ----- Planner day-timeline view -----

export const DAY_MIN = 0; // 00:00 — absolute bounds the visible window can extend to
export const DAY_MAX = 24 * 60; // 24:00
export const DEFAULT_WINDOW_START = 9 * 60; // 09:00 — default visible window
export const DEFAULT_WINDOW_END = 17 * 60; // 17:00
export const WINDOW_STEP_MIN = 60; // extend/contract by one hour per click
export const SNAP_MIN = 15;
export const DEFAULT_DURATION_MIN = 60; // render width for slots with no duration set
export const PX_PER_MIN = 1.4; // fallback scale for drag math before the track is measured

export function snapMinutes(min: number): number {
  return Math.round(min / SNAP_MIN) * SNAP_MIN;
}

export function localMinutesOfDay(iso: string): number {
  const d = parseWallClock(iso);
  return d.getHours() * 60 + d.getMinutes();
}

export function localDateKey(iso: string): string {
  const d = parseWallClock(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A local day-key (YYYY-MM-DD) → a Date at local midnight. */
export function dateFromKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** A local day (YYYY-MM-DD) + minutes-from-midnight → a naive wall-clock string for the API. */
export function dayPlusMinutesToNaive(dayKey: string, minutes: number): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dayKey}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}:00`;
}
